export type CohortHeatCell = {
  age_months: number
  retained_share?: number | null
  retained_mrr?: number | null
  observable: boolean
  months_matured: number
}

export type CohortHeatRow = {
  cohort_month: string
  size: number
  cells: CohortHeatCell[]
}

export type CohortHeatmapProps = {
  ages: number[]
  rows: CohortHeatRow[]
  mode?: 'revenue' | 'logo'
  ariaLabel: string
  className?: string
}

export type CohortCellView = {
  row: number
  col: number
  age: number
  value: number | null
  observable: boolean
  fill: string
}

const TEAL_RAMP = [
  { at: 0.9, fill: 'var(--good-acc, #1c8d97)' },
  { at: 0.8, fill: '#3a9aa3' },
  { at: 0.7, fill: '#6bb4bb' },
  { at: 0.6, fill: '#a3cfd3' },
  { at: 0, fill: 'var(--canvas-2, #efece4)' },
]

export function cohortFill(share: number | null | undefined, observable: boolean): string {
  if (!observable || share == null || !Number.isFinite(share)) return 'url(#ceo-cohort-hatch)'
  const step = TEAL_RAMP.find((entry) => share >= entry.at) || TEAL_RAMP[TEAL_RAMP.length - 1]
  return step.fill
}

export function computeCohortCells(
  ages: number[],
  rows: CohortHeatRow[],
  mode: 'revenue' | 'logo' = 'logo',
): CohortCellView[] {
  const cells: CohortCellView[] = []
  rows.forEach((row, rowIndex) => {
    ages.forEach((age, col) => {
      const cell = row.cells.find((item) => item.age_months === age)
      const value = mode === 'revenue' ? cell?.retained_mrr ?? null : cell?.retained_share ?? null
      const observable = Boolean(cell?.observable)
      cells.push({
        row: rowIndex,
        col,
        age,
        value: value == null || !Number.isFinite(value) ? null : value,
        observable,
        fill: cohortFill(mode === 'revenue' ? (cell?.retained_share ?? null) : value, observable),
      })
    })
  })
  return cells
}

function formatMonth(value: string): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

export function CohortHeatmap({ ages, rows, mode = 'logo', ariaLabel, className }: CohortHeatmapProps) {
  const cells = computeCohortCells(ages, rows, mode)
  const cellW = 36
  const cellH = 28
  const labelW = 72
  const width = labelW + ages.length * cellW + 8
  const height = 24 + rows.length * cellH + 8

  return (
    <svg className={className} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
      <defs>
        <pattern id="ceo-cohort-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--anchor, #9aa0a8)" strokeWidth="1.2" />
        </pattern>
      </defs>
      {ages.map((age, col) => (
        <text key={`age-${age}`} x={labelW + col * cellW + cellW / 2} y={14} textAnchor="middle" fill="var(--muted, #868c9a)" fontSize={10}>
          {age}
        </text>
      ))}
      {rows.map((row, rowIndex) => (
        <g key={row.cohort_month}>
          <text x={labelW - 8} y={24 + rowIndex * cellH + 18} textAnchor="end" fill="var(--ink-2, #4c5360)" fontSize={10}>
            {formatMonth(row.cohort_month)}
          </text>
          {ages.map((age, col) => {
            const cell = cells.find((item) => item.row === rowIndex && item.col === col)
            if (!cell) return null
            const x = labelW + col * cellW
            const y = 20 + rowIndex * cellH
            return (
              <g key={`${row.cohort_month}-${age}`}>
                <rect x={x + 2} y={y + 2} width={cellW - 4} height={cellH - 4} rx={2} fill={cell.fill} />
                {cell.observable && cell.value != null ? (
                  <text x={x + cellW / 2} y={y + 18} textAnchor="middle" fill="var(--ink, #1c1f26)" fontSize={9} fontWeight={600}>
                    {mode === 'revenue' ? Math.round(cell.value).toString() : `${Math.round(cell.value * 100)}`}
                  </text>
                ) : null}
              </g>
            )
          })}
        </g>
      ))}
    </svg>
  )
}

export default CohortHeatmap
